import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Cargando } from './ui/estados';

export interface FichaDeCosto {
  readonly codigo: string;
  readonly nombre: string;
  readonly unidad: string;
  readonly ultimaCompra: {
    readonly costoUnitario: string;
    readonly fecha: string;
    readonly proveedor: string;
  } | null;
  readonly costoDelStock: string | null;
  readonly costoDeReposicion: string | null;
  readonly precioDeVenta: string | null;
  readonly vendido: {
    readonly cantidad: string;
    readonly ingreso: string;
    readonly costo: string;
    readonly margen: string;
    readonly iva: string;
  };
}

/**
 * Cuánto costó, cuánto saldría reponerlo y cuánto se ganó de verdad.
 *
 * Está acá y no adentro de Existencias porque lo miran dos pantallas: el diálogo de la lista y la
 * ficha del artículo. Dos copias de esta lista de números se separan a la primera vez que alguien
 * agrega una fila, y entonces las dos pantallas contestan distinto sobre el mismo artículo.
 */
export function CostoYMargen({ varianteId }: { readonly varianteId: string }): React.ReactElement {
  const { t } = useTranslation();

  const ficha = useQuery({
    queryKey: ['ficha-de-costo', varianteId],
    queryFn: () => pedir<FichaDeCosto>(`/variantes/${varianteId}/costo`),
  });

  if (ficha.isPending) return <Cargando />;
  if (!ficha.isSuccess) return <span />;

  return (
    <dl className="flex flex-col gap-2 text-sm">
      <div className="flex justify-between">
        <dt className="text-texto-tenue">{t('compras.ultimaCompra')}</dt>
        <dd className="numero text-right">
          {ficha.data.ultimaCompra === null
            ? t('compras.sinDatos')
            : ficha.data.ultimaCompra.costoUnitario}
          {ficha.data.ultimaCompra === null ? null : (
            <span className="block text-xs text-texto-tenue">
              {ficha.data.ultimaCompra.proveedor}
            </span>
          )}
        </dd>
      </div>
      <div className="flex justify-between">
        <dt className="text-texto-tenue">{t('compras.costoDelStock')}</dt>
        <dd className="numero">{ficha.data.costoDelStock ?? t('compras.sinDatos')}</dd>
      </div>
      <div className="flex justify-between">
        <dt className="text-texto-tenue">{t('compras.costoDeReposicion')}</dt>
        <dd className="numero">{ficha.data.costoDeReposicion ?? t('compras.sinDatos')}</dd>
      </div>
      <div className="flex justify-between border-t border-borde pt-2">
        <dt className="text-texto-tenue">{t('compras.precioDeVenta')}</dt>
        <dd className="numero">{ficha.data.precioDeVenta ?? t('compras.sinDatos')}</dd>
      </div>

      <div className="mt-2 border-t border-borde pt-2">
        <p className="mb-1 text-xs text-texto-tenue">{t('compras.ayudaMargenReal')}</p>
        <div className="flex justify-between">
          <dt className="text-texto-tenue">{t('compras.vendidoCantidad')}</dt>
          <dd className="numero">{ficha.data.vendido.cantidad}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-texto-tenue">{t('compras.ingreso')}</dt>
          <dd className="numero">{ficha.data.vendido.ingreso}</dd>
        </div>
        {/* El IVA se muestra aparte y no sumado al ingreso: es plata que pasó por la caja del
            comercio pero nunca fue suya. */}
        <div className="flex justify-between">
          <dt className="text-texto-tenue">{t('compras.ivaCobrado')}</dt>
          <dd className="numero">{ficha.data.vendido.iva}</dd>
        </div>
        <div className="flex justify-between font-medium">
          <dt>{t('compras.margenReal')}</dt>
          <dd className="numero">{ficha.data.vendido.margen}</dd>
        </div>
      </div>
    </dl>
  );
}
